import {Component, effect, inject, signal} from '@angular/core';
import {NgClass} from "@angular/common";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {
  CurrentFarmComponent
} from "src/app/presentation/main-view/information-center/current-farm/current-farm.component";
import {
  WaterPointsButtonComponent
} from "src/app/presentation/main-view/information-center/water-points-button/water-points-button.component";
import {
  PrecipitationSamplePointsButtonComponent
} from "src/app/presentation/main-view/information-center/precipitation-sample-points-button/precipitation-sample-points-button.component";
import {
  ProximitySamplePointsButtonComponent
} from "src/app/presentation/main-view/information-center/proximity-sample-points-button/proximity-sample-points-button.component";
import {
  GatewaysButtonComponent
} from "src/app/presentation/main-view/information-center/gateways-button/gateways-button.component";
import {
  AnimalsButtonComponent
} from "src/app/presentation/main-view/information-center/animals-button/animals-button.component";
import {
  GrazingPatternsButtonComponent
} from "src/app/presentation/main-view/information-center/grazing-patterns-button/grazing-patterns-button.component";
import {ActionButtonComponent} from "src/app/presentation/common/action-button/action-button.component";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {NetworkStatusStore} from 'src/app/infrastructure/network/network-status.store';
import {BootstrapService} from 'src/app/infrastructure/init/bootstrap.service';
import {
  UnassignedTagsButtonComponent
} from "src/app/presentation/main-view/information-center/unassigned-tags-button/unassigned-tags-button.component";
import {environment} from 'src/app/environments/environment';
import {DeviceStore} from 'src/app/domain/devices/device.store';
import {GatewayStore} from 'src/app/domain/devices/gateway/gateway.store';
import {AnimalsStore} from 'src/app/domain/assets/animals/animals.store';
import {
  SignalStrengthAnalysisButtonComponent
} from "src/app/presentation/main-view/information-center/signal-strength-analysis-button/signal-strength-analysis-button.component";
import {
  PaddocksButtonComponent
} from "src/app/presentation/main-view/information-center/paddocks-button/paddocks-button.component";
import {MapStore} from 'src/app/domain/map/map.store';
import {FixedSamplePointsStore} from 'src/app/domain/assets/fixed-sample-points/fixed-sample-points.store';
import {AssetsStore} from 'src/app/domain/assets/assets.store';
import {WaterPointsStore} from 'src/app/domain/assets/water-points/water-points.store';
import {
  AnimalPingScheduleButtonComponent
} from "src/app/presentation/main-view/information-center/animal-ping-schedule-button/animal-ping-schedule-button.component";
import {DeviceType} from 'src/app/domain/devices/device';
import {FarmStore} from 'src/app/domain/assets/farm/farm.store';
import {BigButton} from "src/app/presentation/main-view/information-center/big-button/big-button.component";
import {UserStore} from 'src/app/domain/user/user.store';
import {AddDeviceSvgComponent} from "src/app/presentation/common/icons/add-device-svg/add-device-svg.component";
import {AddAnimalSvgComponent} from "src/app/presentation/common/icons/add-animal-svg/add-animal-svg.component";

@Component({
  selector: 'app-information-center',
  imports: [
    NgClass,
    CurrentFarmComponent,
    CurrentFarmComponent,
    WaterPointsButtonComponent,
    PrecipitationSamplePointsButtonComponent,
    ProximitySamplePointsButtonComponent,
    GatewaysButtonComponent,
    AnimalsButtonComponent,
    GrazingPatternsButtonComponent,
    ActionButtonComponent,
    UnassignedTagsButtonComponent,
    SignalStrengthAnalysisButtonComponent,
    PaddocksButtonComponent,
    AnimalPingScheduleButtonComponent,
    BigButton,
    AddDeviceSvgComponent,
    AddAnimalSvgComponent
],
  templateUrl: './information-center.component.html',
  styleUrl: './information-center.component.css'
})
export class InformationCenterComponent {

  showAnimals = signal(false);
  showAnimalPingSchedule = signal(false);
  showLoRaGateways = signal(false);
  showUnassignedTags = signal(false);
  showPrecipitationSamplePoints = signal(false);
  showProximitySamplePoints = signal(false);
  showWaterSamplePoints = signal(false);

  isNativeDevice = !['', 'web'].includes(environment.platform);
  loading = signal(true);

  networkStatusStore = inject(NetworkStatusStore);
  userStore = inject(UserStore);

  private animalStore = inject(AnimalsStore);
  private assetsStore = inject(AssetsStore);
  private deviceStore = inject(DeviceStore);
  private farmStore = inject(FarmStore);
  private fixedSamplePointsStore = inject(FixedSamplePointsStore);
  private gatewayStore = inject(GatewayStore);
  private mapStore = inject(MapStore);
  private waterPointsStore = inject(WaterPointsStore);

  constructor(
    private bootstrapService: BootstrapService,
    protected dataSubscriptionService: DataSubscriptionService,
    protected viewportService: ViewportService)
  {
    this.mapStore.resetLayers();

    effect(() => {
      const unassignedTags = this.deviceStore.unassignedTags();
      const loading = this.deviceStore.loading();
      this.showUnassignedTags.set(!loading && unassignedTags.length > 0);
    });

    effect(() => {
      this.showLoRaGateways.set(!this.gatewayStore.loading() && this.gatewayStore.entities().length > 0);
    });

    effect(() => {
      this.showAnimals.set(!this.animalStore.loading() && this.animalStore.entities().length > 0);
    });

    effect(() => {
      const farm = this.farmStore.farm();
      if (this.animalStore.loading() || this.deviceStore.loading() || !farm) return;

      const hasAnimalsWithGpsTags = this.animalStore.entities().some(animal => {
        const deviceId = this.animalStore.animalIdToDeviceId().get(animal.id);
        if (!deviceId) return false;

        const deviceType = this.deviceStore.deviceIdToType().get(deviceId);

        return deviceType === DeviceType.GPS_PROXIMITY_ACCELEROMETER_TAG || deviceType === DeviceType.GPS_TAG;
      });

      const isLazyEightFarm = farm.id === '5e5b39ab-425e-4563-8faf-c93d5373d25e';

      this.showAnimalPingSchedule.set(isLazyEightFarm && hasAnimalsWithGpsTags);
    });

    effect(() => {
      const loading = this.fixedSamplePointsStore.loading();
      const proximitySamplePoints = this.fixedSamplePointsStore.proximitySamplePoints();
      const unassignedBluetoothProximitySensors = this.deviceStore.unassignedBluetoothProximitySensors();

      this.showProximitySamplePoints.set(!loading && (proximitySamplePoints.length > 0 || unassignedBluetoothProximitySensors.length > 0));

      const precipitationSamplePoints = this.fixedSamplePointsStore.precipitationSamplePoints();
      this.showPrecipitationSamplePoints.set(!loading && precipitationSamplePoints.length > 0);
    });

    effect(() => {
      this.loading.set(this.assetsStore.loading());
    });

    effect(() => {
      if (this.waterPointsStore.loading() || this.deviceStore.loading()) return;

      const waterPoints = this.waterPointsStore.entities();
      const unassignedWaterWaterMonitors = this.deviceStore.unassignedWaterMonitors();
      this.showWaterSamplePoints.set(waterPoints.length > 0 || unassignedWaterWaterMonitors.length > 0);
    });
  }

  refresh() {
    if (!this.networkStatusStore.isOnline()) return;
    this.bootstrapService.refresh();
  }
}
